import styled, { keyframes, css } from 'styled-components';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
  50% { box-shadow: 0 0 15px rgba(64, 128, 255, 0.3); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const particleFloat = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translate(var(--x), var(--y)) rotate(var(--r)); opacity: 0; }
`;

const scanEffect = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const hologramFlicker = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  52% { opacity: 0.9; }
  54% { opacity: 0.7; }
  100% { opacity: 1; }
`;

const quantumLoading = keyframes`
  0% { transform: scale(1); opacity: 0.7; box-shadow: 0 0 10px rgba(64, 128, 255, 0.3); }
  50% { transform: scale(1.2); opacity: 1; box-shadow: 0 0 20px rgba(64, 128, 255, 0.6); }
  100% { transform: scale(1); opacity: 0.7; box-shadow: 0 0 10px rgba(64, 128, 255, 0.3); }
`;

const codeFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 0% 100%; }
`;

const digitalDistortion = keyframes`
  0% { clip-path: inset(0 0 0 0); }
  5% { clip-path: inset(10% 0 20% 0); }
  10% { clip-path: inset(40% 0 5% 0); }
  15% { clip-path: inset(0 0 30% 0); }
  20% { clip-path: inset(25% 0 0 0); }
  25% { clip-path: inset(0 0 0 0); }
  100% { clip-path: inset(0 0 0 0); }
`;

const glitch = keyframes`
  0% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  2% {
    clip-path: inset(10% 0 20% 0);
    transform: translate(-2px);
  }
  4% {
    clip-path: inset(40% 0 5% 0);
    transform: translate(2px);
  }
  6% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

const scanLine = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

// Componentes estilizados
export const ProductContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 10% 10%, rgba(64, 128, 255, 0.1), transparent 40%),
      radial-gradient(circle at 90% 90%, rgba(0, 200, 255, 0.1), transparent 40%);
    z-index: -1;
    pointer-events: none;
  }
`;

export const ProductHeader = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const ProductTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(to right, #64B6FF, #00FFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.3);
`;

export const ProductSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
`;

export const ProductCard = styled.div`
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  border: 1px solid rgba(64, 128, 255, 0.2);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  animation: ${glowPulse} 4s infinite;
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

export const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(64, 128, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(64, 128, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: 0;
  pointer-events: none;
`;

export const DataStream = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, rgba(64, 128, 255, 0.5), transparent);
    animation: ${dataFlow} 8s infinite;
    opacity: 0;
  }
  
  &::before {
    left: 20%;
    animation-delay: 0s;
  }
  
  &::after {
    left: 80%;
    animation-delay: 4s;
  }
`;

export const TechCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.size || '100%'};
  height: ${props => props.size || '100%'};
  border-radius: 50%;
  border: 1px solid ${props => props.color || 'rgba(64, 128, 255, 0.2)'};
  animation: ${pulse} ${props => 5 + props.delay}s infinite;
  animation-delay: ${props => props.delay || 0}s;
  z-index: 0;
  pointer-events: none;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.5s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:focus {
    outline: none;
    border-color: #4C6EF5;
    box-shadow: 0 0 0 2px rgba(76, 110, 245, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, transparent, #4C6EF5, transparent);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  &:focus::after {
    transform: translateX(100%);
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #4C6EF5, #64B6FF);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    background: rgba(64, 128, 255, 0.3);
    cursor: not-allowed;
    transform: translateY(0);
    box-shadow: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

export const ErrorMessage = styled.div`
  color: #FF5252;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const SuccessMessage = styled.div`
  color: #00E676;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: ${rotate} 1s linear infinite;
  margin-right: 0.5rem;
`;

export const Logo = styled.img`
  width: 80px;
  height: auto;
  margin-bottom: 1rem;
  filter: drop-shadow(0 0 10px rgba(64, 128, 255, 0.5));
`;

export const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

export const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '5px'};
  height: ${props => props.size || '5px'};
  background: ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  border-radius: 50%;
  top: ${props => props.top || '50%'};
  left: ${props => props.left || '50%'};
  --x: ${props => props.x || '100px'};
  --y: ${props => props.y || '100px'};
  --r: ${props => props.r || '180deg'};
  animation: ${particleFloat} ${props => props.duration || '3s'} ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
`;

export const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, transparent, #4C6EF5, transparent);
  opacity: 0;
  animation: ${scanEffect} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 2;
  pointer-events: none;
`;

export const HologramEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, transparent 0%, rgba(64, 128, 255, 0.05) 50%, transparent 100%);
  animation: ${hologramFlicker} 5s infinite;
  z-index: 1;
  pointer-events: none;
`;

export const QuantumLoader = styled.div`
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: radial-gradient(circle, #4C6EF5, #64B6FF);
  animation: ${quantumLoading} 1.5s infinite;
  margin: 0 auto;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: inherit;
    opacity: 0.7;
    animation: ${pulse} 1.5s infinite;
  }
  
  &::before {
    animation-delay: 0.5s;
  }
  
  &::after {
    animation-delay: 1s;
  }
`;

export const CodeFlowBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, transparent 97%, rgba(64, 128, 255, 0.2) 3%);
  background-size: 100% 20px;
  animation: ${codeFlow} 30s linear infinite;
  z-index: 0;
  pointer-events: none;
  opacity: 0.2;
`;

export const DigitalDistortionEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(64, 128, 255, 0.05);
  animation: ${digitalDistortion} 10s infinite;
  z-index: 1;
  pointer-events: none;
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 0.3s ease;
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  option {
    background-color: #1F2937;
  }
`;

export const RangeValue = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  position: relative;
  
  span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    
    &:nth-child(2) {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(20, 30, 48, 0.8);
      color: #64B6FF;
      padding: 8px 15px;
      border-radius: 4px;
      font-weight: bold;
      font-family: 'Courier New', monospace;
      letter-spacing: 1px;
      border: 1px solid rgba(100, 182, 255, 0.5);
      box-shadow: 
        0 0 10px rgba(100, 182, 255, 0.5),
        inset 0 0 5px rgba(100, 182, 255, 0.3);
      text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
      animation: ${pulse} 2s infinite ease-in-out, ${glitch} 10s infinite;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          to bottom,
          transparent 0%,
          rgba(100, 182, 255, 0.1) 50%,
          transparent 100%
        );
        animation: ${scanLine} 2s linear infinite;
        z-index: 1;
        pointer-events: none;
      }
      
      &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 2px;
        height: 6px;
        background: rgba(100, 182, 255, 0.8);
        box-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
      }
    }
  }
`;

export const NumberSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  overflow: hidden;
`;

export const NumberButton = styled.button`
  background: rgba(0, 0, 0, 0.3);
  border: none;
  color: #fff;
  font-size: 1.2rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(76, 110, 245, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const NumberDisplay = styled.div`
  flex: 1;
  text-align: center;
  font-size: 1.2rem;
  padding: 0.5rem;
  color: #fff;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(64, 128, 255, 0.1), transparent);
    animation: ${shimmer} 2s infinite;
  }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px dashed rgba(64, 128, 255, 0.5);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.8rem;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: rgba(64, 128, 255, 0.1);
    border-color: rgba(64, 128, 255, 0.8);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuestionCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

export const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(255, 82, 82, 0.2);
  border: none;
  color: #FF5252;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  
  &:hover {
    background: rgba(255, 82, 82, 0.4);
    transform: scale(1.1);
  }
`;

export const AIGenerationContainer = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.5rem;
  overflow: hidden;
`;

export const AIGenerationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
`;

export const AIGenerationContent = styled.div`
  position: relative;
  min-height: 0;
`;

export const NeuralAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: ${props => props.active ? 'block' : 'none'};
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 10px 10px, rgba(64, 128, 255, 0.2) 2px, transparent 2px);
    background-size: 20px 20px;
    animation: ${pulse} 2s infinite;
  }
`;

export const NeuralConnection = styled.div`
  position: absolute;
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
  width: ${props => props.width || '50px'};
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(64, 128, 255, 0.5), transparent);
  transform: rotate(${props => props.rotate || '0deg'});
  animation: ${pulse} 2s infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

export const GenerateButton = styled(Button)`
  margin-top: 1rem;
  background: linear-gradient(to right, #7B68EE, #4C6EF5);
  
  &:hover {
    background: linear-gradient(to right, #9370DB, #7B68EE);
  }
`;

export const ProgressIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 2rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    background: rgba(64, 128, 255, 0.2);
    transform: translateY(-50%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: ${props => ((props.currentStep - 1) / (props.totalSteps - 1)) * 100}%;
    height: 2px;
    background: linear-gradient(to right, #4C6EF5, #64B6FF);
    transform: translateY(-50%);
    z-index: 1;
    transition: width 0.3s ease;
  }
`;

export const ProgressStep = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => {
    if (props.completed) return 'linear-gradient(to right, #4C6EF5, #64B6FF)';
    if (props.active) return 'rgba(64, 128, 255, 0.2)';
    return 'rgba(0, 0, 0, 0.2)';
  }};
  border: 2px solid ${props => {
    if (props.completed || props.active) return '#4C6EF5';
    return 'rgba(64, 128, 255, 0.2)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.completed ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: bold;
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  
  &::before {
    content: attr(data-step);
  }
  
  ${props => props.completed && css`
    &::before {
      content: '✓';
    }
  `}
  
  ${props => (props.active || props.completed) && css`
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.2);
  `}
`;

export const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, #4C6EF5, transparent);
  }
`;
